import { CheckOutlined, CloseOutlined } from "@ant-design/icons"
import { App, Button, DatePicker, Flex, Form, Tag } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import dayjs from "dayjs"
import React, { useState } from "react"

import {
  type CellEditors,
  buildEditRowCells,
  rowCommitKeyDown
} from "../../../../components/EntityTable/editable-row-cells.ts"
import { useRowEscapeCancel } from "../../../../components/EntityTable/use-row-escape-cancel.ts"
import { MODAL_CONFIRM_CONFIG } from "../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { FormChangedWatcher } from "../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import InputNumber from "../../../../components/Input/InputNumber.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { excludeDeletedAccounts } from "../../../../constants/deleted-account-ui.ts"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { buildEntityLabelMap } from "../../../../utils/select/entity-labels.ts"
import { ManagersSelector } from "../../../Project/ProjectAddPage/components/sections/StakeholdersAndRolesSection/ManagersSelector.tsx"
import { PROJECT_STATUSES_MAP } from "../../constants/project-statuses.ts"
import { type ProjectListItem } from "../../types/types.ts"
import { PROJECT_COLUMN_KEYS } from "./columns.tsx"

export type ProjectEditableRowData = ProjectListItem

interface ProjectListEditableRowProps {
  record: ProjectListItem
  editingID: string | null
  assignedPMAllowed?: boolean
  /** Without it the price stays as shown: changing it moves the contract value. */
  priceEditAllowed?: boolean
  filterOptions: Record<string, BaseOptionType[]>
  children: React.ReactNode[]
  onCancelEdit: () => void
  onUpdateRow: (data: ProjectEditableRowData) => Promise<void>
  /** May arrive via the table's `onRow`; composed with the row's own handler. */
  onKeyDown?: React.KeyboardEventHandler<HTMLTableRowElement>
}

export const ProjectListEditableRow = ({
  editingID,
  filterOptions,
  record,
  priceEditAllowed,
  ...rest
}: ProjectListEditableRowProps) => {
  // A row antd renders itself (an expanded details row) carries neither a
  // record nor an editing id, and `undefined === undefined` is not an edit.
  const isEditing = !!record && !!editingID && record.id === editingID

  if (!isEditing) return <tr {...rest}>{rest?.children}</tr>

  return (
    <ProjectListEditableRowForm
      record={record}
      filterOptions={filterOptions}
      priceEditAllowed={priceEditAllowed}
      {...rest}
    />
  )
}

interface ProjectListEditableRowFormProps {
  record: ProjectListItem
  filterOptions: Record<string, BaseOptionType[]>
  onCancelEdit: () => void
  assignedPMAllowed?: boolean
  priceEditAllowed?: boolean
  children: React.ReactNode[]
  onUpdateRow: (data: ProjectEditableRowData) => Promise<void>
  /** May arrive via the table's `onRow`; composed with the row's own handler. */
  onKeyDown?: React.KeyboardEventHandler<HTMLTableRowElement>
}

const ProjectListEditableRowForm = ({
  record,
  filterOptions,
  assignedPMAllowed,
  priceEditAllowed,
  onCancelEdit,
  onUpdateRow,
  ...rest
}: ProjectListEditableRowFormProps) => {
  const { getDateValueProps, normalizeDate } = useDateFormat()
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectEditableRowData>()
  const { modal } = App.useApp()

  const statusData = PROJECT_STATUSES_MAP?.[record?.project_status]

  const handleSave = async () => {
    try {
      setSaving(true)
      const values = await form.validateFields()
      await onUpdateRow({
        ...values,
        end_date: normalizeDate(values?.end_date),
        id: record?.id
      })
      onCancelEdit()
    } catch (error) {
      console.error("Validation failed:", error)
    } finally {
      setSaving(false)
    }
  }

  // Asks only when the user changed a field. Comparing the form with the record
  // read every column the layout hides (Due Date by default) as cleared, so
  // cancelling an untouched row always asked.
  const onCancelHandler = () => {
    if (form.isFieldsTouched()) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk: onCancelEdit
      })
    } else {
      onCancelEdit()
    }
  }

  const editors: CellEditors = {
    [PROJECT_COLUMN_KEYS.leadName]: (
      <td className="ant-table-cell">
        <Form.Item<ProjectEditableRowData>
          className="px-0 m-0"
          name="lead_name"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Project name is required"
            }
          ]}
        >
          <TextInput placeholder="Enter project name" />
        </Form.Item>
      </td>
    ),
    [PROJECT_COLUMN_KEYS.endDate]: (
      <td className="ant-table-cell">
        <Form.Item<ProjectEditableRowData>
          className="px-0 m-0"
          name="end_date"
          getValueProps={getDateValueProps}
        >
          <DatePicker className="w-full" />
        </Form.Item>
      </td>
    ),
    [PROJECT_COLUMN_KEYS.projectStatus]: (
      <td className="ant-table-cell">
        <Tag style={statusData?.style} className="m-0 cursor-pointer">
          {statusData?.label || "No status"}
        </Tag>
      </td>
    ),
    ...(priceEditAllowed
      ? {
          [PROJECT_COLUMN_KEYS.salesPrice]: (
            <td className="ant-table-cell">
              <Form.Item<ProjectEditableRowData>
                name="sales_price"
                className="px-0 m-0"
                getValueFromEvent={(value) => value ?? null}
              >
                <InputNumber
                  prefix="$"
                  placeholder="0.00"
                  step={0.01}
                  className="w-full"
                />
              </Form.Item>
            </td>
          )
        }
      : {}),
    ...(assignedPMAllowed
      ? {
          [PROJECT_COLUMN_KEYS.projectManagers]: (
            <td className="ant-table-cell">
              <Form.Item<ProjectEditableRowData>
                className="px-0 m-0"
                name="project_managers"
              >
                <ManagersSelector
                  options={filterOptions?.project_manager}
                  fallbackLabels={buildEntityLabelMap(record?.project_managers)}
                />
              </Form.Item>
            </td>
          )
        }
      : {})
  }

  // Escape leaves the row the way its ✕ does, from the moment it opens.
  useRowEscapeCancel(onCancelHandler, saving)

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    incoming: rest.onKeyDown,
    busy: saving,
    cancel: onCancelHandler
  })

  const actionsCell = (
    <td className="sticky right-0 ant-table-cell ant-table-cell-fix-right ant-table-cell-fix-right-first">
      <Flex gap="small" justify="center" className="mx-auto">
        <Button
          variant="outlined"
          color="danger"
          onClick={onCancelHandler}
          disabled={saving}
          icon={<CloseOutlined />}
        />
        <Button
          variant="outlined"
          color="green"
          loading={saving}
          onClick={handleSave}
          icon={<CheckOutlined />}
        />
      </Flex>
    </td>
  )

  return (
    <Form
      form={form}
      component={false}
      initialValues={{
        ...record,
        project_managers: excludeDeletedAccounts(record?.project_managers),
        end_date: record?.end_date ? dayjs(record.end_date) : undefined
      }}
      disabled={saving}
    >
      <FormChangedWatcher<ProjectEditableRowData>
        form={form}
        saving={saving}
        debug
      />

      <tr {...rest} onKeyDown={onKeyDownRow}>
        {buildEditRowCells(
          rest?.children,
          Object.values(PROJECT_COLUMN_KEYS),
          editors,
          actionsCell
        )}
      </tr>
    </Form>
  )
}
